import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import {
  AnalyticsService,
  NgxBarGroup,
} from '../../services/api';
import { Color } from '@swimlane/ngx-charts';


type MostWatchedRequest = {
  metric?: 'total' | 'reached' | 'distinctUsers';
  topN?: number;
  maxSeriesPerBar?: number;
  // optional filters if you use them:
  grade?: string; level?: string; theme?: string; tag?: string;
  publisher?: string; hasQuiz?: boolean; quizId?: string;
  startDateUtc?: string; endDateUtc?: string;
};

@Component({
  selector: 'app-most-watched',
  templateUrl: './most-watched.component.html',
  styleUrls: ['./most-watched.component.scss']
})
export class MostWatchedComponent implements OnInit, OnChanges {
  @Input() viewsize: [number, number] = [1000, 520];
  @Input() disableResize: boolean = false;
  // When true, render as stacked horizontal bars instead of grouped 2D
  @Input() useStacked: boolean = false;
  // Date range from parent dashboard
  @Input() startDate?: Date | null;
  @Input() endDate?: Date | null;

  showXAxis = true;
  showYAxis = true;
  showLegend = true;
  showXAxisLabel = true;
  showYAxisLabel = true;
  // For horizontal charts, x-axis is the value axis
  xAxisLabel = 'Views';
  yAxisLabel = 'Video';

  chartData: NgxBarGroup[] = [];
  loading = false;
  errorMsg = '';
  // enforce legend order and allowed grades
  private readonly legendOrder: string[] = ['PP1', 'PP2', 'Grade 1', 'Grade 2'];

  // Explicit custom colors for Grade 1 and Grade 2
  customColors = [
    { name: 'Grade 1', value: '#f0a617' },
    { name: 'Grade 2', value: '#15a9a8' },
  ];

  constructor(private analyticsService: AnalyticsService) {}

  ngOnInit(): void {
    this.fetchMostWatched();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['startDate'] || changes['endDate']) {
      this.fetchMostWatched();
    }
  }

  private fetchMostWatched(): void {
    this.loading = true;
    this.errorMsg = '';

    const body: MostWatchedRequest = {
      metric: 'total',    // or 'reached' | 'distinctUsers'
      topN: 10,
      maxSeriesPerBar: 6,
      startDateUtc: this.startDate ? this.startDate.toISOString() : undefined,
      endDateUtc: this.endDate ? this.endDate.toISOString() : undefined
      // leave grade/level empty => all grades & levels
    };

    this.analyticsService.analyticsGetMostWatchedVideosByGradeLevelPost(body).subscribe({
      next: data => {
  const raw = data ?? [];
  this.chartData = this.normalizeSeries(raw);
        this.loading = false;
        if (this.disableResize) {
          setTimeout(() => {
            this.viewsize = [this.viewsize[0], this.viewsize[1]];
          });
        }
      },
      error: err => {
        console.error(err);
        this.errorMsg = 'Failed to load data.';
        this.loading = false;
      }
    });
  }

  // keep titles readable on the X axis
  formatVideoLabel = (title: string) => {
    if (!title) return '';
    const max = 18; // tweak
    return title.length > max ? title.slice(0, max - 1) + '…' : title;
  };

  onResize(event: any) {
  if (this.disableResize) return;
  const w = event?.target?.innerWidth ?? 1200;
  this.viewsize = [Math.max(720, Math.floor(w * 0.8)), 520];
  }

  // Keep only the allowed grades and order them as specified
  private normalizeSeries(groups: NgxBarGroup[]): NgxBarGroup[] {
    // patterns for case-insensitive contains() matching
    const patterns: Record<string, RegExp[]> = {
      'PP1': [/(^|\W)pp1(\W|$)/i],
      'PP2': [/(^|\W)pp2(\W|$)/i],
      'Grade 1': [/grade\s*1/i],
      'Grade 2': [/grade\s*2/i],
    };

    const matchCanonical = (label: string): string | null => {
      const src = (label || '').toString();
      for (const canonical of this.legendOrder) {
        const tests = patterns[canonical] || [];
        if (tests.some(rx => rx.test(src))) return canonical;
      }
      return null;
    };

    return (groups || []).map(g => {
      const seriesMap = new Map<string, number>(this.legendOrder.map(k => [k, 0]));
      (g.series || []).forEach(s => {
        const canonical = matchCanonical(s?.name as string);
        if (!canonical) return; // ignore anything not matching our targets
        const current = seriesMap.get(canonical) ?? 0;
        seriesMap.set(canonical, current + Number(s?.value ?? 0));
      });

      const ordered = this.legendOrder.map(name => ({
        name,
        value: seriesMap.get(name) ?? 0
      }));

      return { name: g.name, series: ordered } as NgxBarGroup;
    });
  }
}
